"use client";

import { useState, useEffect } from "react";
import {
  FaExchangeAlt,
  FaCheck,
  FaTimes,
  FaClock,
  FaHistory,
  FaList,
  FaCalendar,
  FaMoneyBillWave,
  FaUniversity,
  FaEdit,
} from "react-icons/fa";

const InternalTransfers = () => {
  const [transfers, setTransfers] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("transfers");
  const [showForm, setShowForm] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);

  const [chequeStatusModal, setChequeStatusModal] = useState({
    show: false,
    chequeId: null,
    status: null,
    notes: "",
  });

  const [rescheduleModal, setRescheduleModal] = useState({
    show: false,
    chequeId: null,
    oldChequePaymentId: null,
    oldChequeNumber: "",
    newChequeNumber: "",
    newChequeDate: "",
    newBankName: "",
    newBankBranch: "",
    notes: "",
  });

  const [formData, setFormData] = useState({
    fromAssetType: "",
    fromAssetId: "",
    toAssetType: "",
    toAssetId: "",
    amount: "",
    reason: "",
    paymentMethod: "",
    chequeNumber: "",
    chequeDate: "",
    bankName: "",
    bankBranch: "",
    cardTransactionId: "",
    cardBankName: "",
    cardHolderName: "",
    fromBankDetails: "",
    toBankDetails: "",
    transactionReference: "",
  });

  useEffect(() => {
    fetchTransfers();
    fetchCheques();
    fetchAccounts();
  }, []);

  const fetchTransfers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/internal-transfers`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setTransfers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching transfers:", error);
      setTransfers([]);
    }
  };

  const fetchCheques = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/internal-cheques`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setCheques(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching cheques:", error);
      setCheques([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      const [cashRes, bankRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash`,
          {
            credentials: "include",
          }
        ),
        fetch(
          `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank`,
          {
            credentials: "include",
          }
        ),
      ]);
      const cashData = await cashRes.json();
      const bankData = await bankRes.json();

      // Extract the actual arrays from the response objects
      setCashAccounts(
        Array.isArray(cashData?.cash_accounts) ? cashData.cash_accounts : []
      );
      setBankAccounts(
        Array.isArray(bankData?.bank_accounts) ? bankData.bank_accounts : []
      );
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setCashAccounts([]);
      setBankAccounts([]);
    }
  };

  // Helper functions to get account details
  const getFromAccount = () => {
    if (!formData.fromAssetType || !formData.fromAssetId) return null;
    const accounts =
      formData.fromAssetType === "cash" ? cashAccounts : bankAccounts;
    return accounts.find((acc) => acc.id === parseInt(formData.fromAssetId));
  };

  const getToAccount = () => {
    if (!formData.toAssetType || !formData.toAssetId) return null;
    const accounts =
      formData.toAssetType === "cash" ? cashAccounts : bankAccounts;
    return accounts.find((acc) => acc.id === parseInt(formData.toAssetId));
  };

  // Real-time validation feedback
  const getValidationMessage = () => {
    if (!formData.amount || !getFromAccount()) return null;

    const transferAmount = parseFloat(formData.amount);
    const fromAccount = getFromAccount();
    const toAccount = getToAccount();

    if (transferAmount <= 0) return null;

    // Check balance for non-cheque payments
    if (formData.paymentMethod !== "cheque") {
      const currentBalance = parseFloat(fromAccount.amount);
      if (currentBalance < transferAmount) {
        return {
          type: "error",
          message: `Insufficient balance. Available: $${currentBalance.toFixed(
            2
          )}`,
        };
      }

      // Check minimum limit
      if (formData.fromAssetType === "cash" && fromAccount.min_limit) {
        const minLimit = parseFloat(fromAccount.min_limit);
        const remainingBalance = currentBalance - transferAmount;
        if (remainingBalance < minLimit) {
          return {
            type: "error",
            message: `Would violate minimum limit ($${minLimit.toFixed(
              2
            )}). Remaining: $${remainingBalance.toFixed(2)}`,
          };
        }
      }
    }

    // Check maximum limit on receiving account
    if (toAccount && formData.toAssetType === "cash" && toAccount.max_limit) {
      const maxLimit = parseFloat(toAccount.max_limit);
      const currentToBalance = parseFloat(toAccount.amount);
      const newBalance = currentToBalance + transferAmount;
      if (newBalance > maxLimit) {
        return {
          type: "error",
          message: `Would exceed maximum limit ($${maxLimit.toFixed(
            2
          )}). New balance: $${newBalance.toFixed(2)}`,
        };
      }
    }

    return { type: "success", message: "Transfer amount is valid" };
  };

  // Validation functions
  const validateTransfer = () => {
    const {
      fromAssetType,
      fromAssetId,
      toAssetType,
      toAssetId,
      amount,
      paymentMethod,
    } = formData;

    // Basic validation
    if (
      !fromAssetType ||
      !fromAssetId ||
      !toAssetType ||
      !toAssetId ||
      !amount ||
      !paymentMethod
    ) {
      return "All fields are required";
    }

    const transferAmount = parseFloat(amount);
    if (transferAmount <= 0) {
      return "Amount must be greater than 0";
    }

    // Get from account details
    const fromAccounts = fromAssetType === "cash" ? cashAccounts : bankAccounts;
    const fromAccount = fromAccounts.find(
      (acc) => acc.id === parseInt(fromAssetId)
    );

    if (!fromAccount) {
      return "From account not found";
    }

    // Get to account details
    const toAccounts = toAssetType === "cash" ? cashAccounts : bankAccounts;
    const toAccount = toAccounts.find((acc) => acc.id === parseInt(toAssetId));

    if (!toAccount) {
      return "To account not found";
    }

    // Validation 1: Check if from account has sufficient balance
    // (Skip this check for cheque payments as they are processed later)
    if (paymentMethod !== "cheque") {
      const currentBalance = parseFloat(fromAccount.amount);
      if (currentBalance < transferAmount) {
        return `Insufficient balance. Available: $${currentBalance.toFixed(2)}`;
      }

      // Validation 3: Check minimum limit on from account (cash only)
      if (fromAssetType === "cash" && fromAccount.min_limit) {
        const minLimit = parseFloat(fromAccount.min_limit);
        const remainingBalance = currentBalance - transferAmount;
        if (remainingBalance < minLimit) {
          return `Transfer would violate minimum limit. Remaining balance ($${remainingBalance.toFixed(
            2
          )}) would be below minimum limit ($${minLimit.toFixed(2)})`;
        }
      }
    }

    // Validation 2: Check maximum limit on to account (cash only)
    if (toAssetType === "cash" && toAccount.max_limit) {
      const maxLimit = parseFloat(toAccount.max_limit);
      const currentToBalance = parseFloat(toAccount.amount);
      const newBalance = currentToBalance + transferAmount;
      if (newBalance > maxLimit) {
        return `Transfer would exceed maximum limit. New balance ($${newBalance.toFixed(
          2
        )}) would exceed maximum limit ($${maxLimit.toFixed(2)})`;
      }
    }

    return null; // No validation errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Perform validation
    const validationResult = validateTransfer();
    if (validationResult && validationResult.type === "error") {
      alert(validationResult.message);
      return;
    }

    try {
      setLoading(true);
      const method = editingTransfer ? "PUT" : "POST";
      const url = editingTransfer
        ? `${process.env.REACT_APP_BASE_URL}/api/assets/internal-transfers/${editingTransfer.id}`
        : `${process.env.REACT_APP_BASE_URL}/api/assets/internal-transfers`;

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchTransfers();
        fetchCheques();
        setShowForm(false);
        setEditingTransfer(null);
        resetForm();
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to save transfer");
      }
    } catch (error) {
      console.error("Error saving transfer:", error);
      alert("An error occurred while saving the transfer");
    } finally {
      setLoading(false);
    }
  };

  const handleChequeStatusUpdate = async (chequeId, status, notes = "") => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/internal-cheques/clear`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            cheque_id: chequeId,
            status,
            notes: notes || null,
          }),
        }
      );

      if (response.ok) {
        fetchCheques();
        fetchTransfers();
        setChequeStatusModal({
          show: false,
          chequeId: null,
          status: null,
          notes: "",
        });
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to update cheque status");
      }
    } catch (error) {
      console.error("Error updating cheque status:", error);
    } finally {
      setLoading(false);
    }
  };

  const openChequeStatusModal = (chequeId, status) => {
    setChequeStatusModal({
      show: true,
      chequeId,
      status,
      notes: "",
    });
  };

  const handleChequeStatusConfirm = () => {
    handleChequeStatusUpdate(
      chequeStatusModal.chequeId,
      chequeStatusModal.status,
      chequeStatusModal.notes
    );
  };

  const openRescheduleModal = (cheque) => {
    setRescheduleModal({
      show: true,
      chequeId: cheque.id,
      oldChequePaymentId: cheque.payment_id,
      oldChequeNumber: cheque.cheque_number,
      newChequeNumber: "",
      newChequeDate: "",
      newBankName: cheque.bank_name || "",
      newBankBranch: cheque.bank_branch || "",
      notes: "",
    });
  };

  console.log(cheques);

  const handleRescheduleSubmit = async () => {
    try {
      setLoading(true);

      if (!rescheduleModal.newChequeNumber || !rescheduleModal.newChequeDate) {
        alert("Please fill in the new cheque number and date");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/internal-cheques/reschedule`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            old_cheque_id: rescheduleModal.chequeId,
            old_cheque_payment_id: rescheduleModal.oldChequePaymentId,
            new_cheque_number: rescheduleModal.newChequeNumber,
            new_cheque_date: rescheduleModal.newChequeDate,
            new_bank_name: rescheduleModal.newBankName,
            new_bank_branch: rescheduleModal.newBankBranch,
            notes: rescheduleModal.notes,
          }),
        }
      );

      if (response.ok) {
        fetchCheques();
        fetchTransfers();
        setRescheduleModal({
          show: false,
          chequeId: null,
          oldChequeNumber: "",
          newChequeNumber: "",
          newChequeDate: "",
          newBankName: "",
          newBankBranch: "",
          notes: "",
        });
        alert("Cheque rescheduled successfully");
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to reschedule cheque");
      }
    } catch (error) {
      console.error("Error rescheduling cheque:", error);
      alert("An error occurred while rescheduling the cheque");
    } finally {
      setLoading(false);
    }
  };

  const fetchChequeHistory = async (chequeId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/internal-cheques/${chequeId}/history`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const history = await response.json();
        const historyText = history
          .map(
            (h) =>
              `${h.cheque_type}: ${h.cheque_number} (${
                h.cheque_date
              }) - Status: ${h.status || "Pending"}`
          )
          .join("\n");
        alert(`Cheque History:\n${historyText}`);
      }
    } catch (error) {
      console.error("Error fetching cheque history:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      fromAssetType: "",
      fromAssetId: "",
      toAssetType: "",
      toAssetId: "",
      amount: "",
      reason: "",
      paymentMethod: "",
      chequeNumber: "",
      chequeDate: "",
      bankName: "",
      bankBranch: "",
      cardTransactionId: "",
      cardBankName: "",
      cardHolderName: "",
      fromBankDetails: "",
      toBankDetails: "",
      transactionReference: "",
    });
  };

  // Helper functions for status management
  const canUpdateChequeStatus = (chequeDate) => {
    const today = new Date();
    const cheque = new Date(chequeDate);
    return cheque <= today;
  };

  const canRescheduleCheque = (cheque) => {
    return cheque.status !== "cleared" && !cheque.next_cheque_number;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "cleared":
        return "text-green-600 bg-green-100";
      case "bounced":
        return "text-red-600 bg-red-100";
      case "expired":
        return "text-gray-600 bg-gray-100";
      case "rescheduled":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-blue-600 bg-blue-100";
    }
  };

  const renderTransferForm = () => (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4">Create Internal Transfer</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            From Asset Type
          </label>
          <select
            value={formData.fromAssetType}
            onChange={(e) =>
              setFormData({
                ...formData,
                fromAssetType: e.target.value,
                fromAssetId: "",
              })
            }
            className="w-full p-2 border rounded"
            required
          >
            <option value="">Select Type</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">From Account</label>
          <select
            value={formData.fromAssetId}
            onChange={(e) =>
              setFormData({ ...formData, fromAssetId: e.target.value })
            }
            className="w-full p-2 border rounded"
            required
          >
            <option value="">Select Account</option>
            {formData.fromAssetType === "cash" &&
              Array.isArray(cashAccounts) &&
              cashAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            {formData.fromAssetType === "bank" &&
              Array.isArray(bankAccounts) &&
              bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.bank_name} - {account.account_number}
                </option>
              ))}
          </select>
          {/* Display from account information */}
          {getFromAccount() && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="font-medium">Account Details:</div>
              <div>
                Current Balance:
                {parseFloat(getFromAccount().amount).toFixed(2)}
              </div>
              {formData.fromAssetType === "cash" &&
                getFromAccount().min_limit && (
                  <div>
                    Minimum Limit:
                    {parseFloat(getFromAccount().min_limit).toFixed(2)}
                  </div>
                )}
              {formData.fromAssetType === "cash" &&
                getFromAccount().max_limit && (
                  <div>
                    Maximum Limit:
                    {parseFloat(getFromAccount().max_limit).toFixed(2)}
                  </div>
                )}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            To Asset Type
          </label>
          <select
            value={formData.toAssetType}
            onChange={(e) =>
              setFormData({
                ...formData,
                toAssetType: e.target.value,
                toAssetId: "",
              })
            }
            className="w-full p-2 border rounded"
            required
          >
            <option value="">Select Type</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">To Account</label>
          <select
            value={formData.toAssetId}
            onChange={(e) =>
              setFormData({ ...formData, toAssetId: e.target.value })
            }
            className="w-full p-2 border rounded"
            required
          >
            <option value="">Select Account</option>
            {formData.toAssetType === "cash" &&
              Array.isArray(cashAccounts) &&
              cashAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            {formData.toAssetType === "bank" &&
              Array.isArray(bankAccounts) &&
              bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.bank_name} - {account.account_number}
                </option>
              ))}
          </select>
          {/* Display to account information */}
          {getToAccount() && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
              <div className="font-medium">Receiving Account Details:</div>
              <div>
                Current Balance: {parseFloat(getToAccount().amount).toFixed(2)}
              </div>
              {formData.toAssetType === "cash" && getToAccount().max_limit && (
                <div>
                  Maximum Limit:
                  {parseFloat(getToAccount().max_limit).toFixed(2)}
                </div>
              )}
              {formData.toAssetType === "cash" && getToAccount().min_limit && (
                <div>
                  Minimum Limit:
                  {parseFloat(getToAccount().min_limit).toFixed(2)}
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Amount</label>
          <input
            type="number"
            step="0.01"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: e.target.value })
            }
            className="w-full p-2 border rounded"
            required
          />
          {/* Real-time validation feedback */}
          {formData.amount && getValidationMessage() && (
            <div
              className={`mt-2 p-2 border rounded text-sm ${
                getValidationMessage().type === "error"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-green-50 border-green-200 text-green-700"
              }`}
            >
              {getValidationMessage().message}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Payment Method
          </label>
          <select
            value={formData.paymentMethod}
            onChange={(e) =>
              setFormData({ ...formData, paymentMethod: e.target.value })
            }
            className="w-full p-2 border rounded"
            required
          >
            <option value="">Select Method</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cheque">Cheque</option>
            <option value="card">Card</option>
          </select>
        </div>
        {formData.paymentMethod === "cheque" && (
          <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-md font-semibold mb-3">Cheque Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cheque Number
                </label>
                <input
                  type="text"
                  value={formData.chequeNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, chequeNumber: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={formData.paymentMethod === "cheque"}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cheque Date
                </label>
                <input
                  type="date"
                  value={formData.chequeDate}
                  onChange={(e) =>
                    setFormData({ ...formData, chequeDate: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={formData.paymentMethod === "cheque"}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) =>
                    setFormData({ ...formData, bankName: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Branch
                </label>
                <input
                  type="text"
                  value={formData.bankBranch}
                  onChange={(e) =>
                    setFormData({ ...formData, bankBranch: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {formData.paymentMethod === "card" && (
          <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-md font-semibold mb-3">Card Payment Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Transaction ID
                </label>
                <input
                  type="text"
                  value={formData.cardTransactionId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cardTransactionId: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Bank Name
                </label>
                <input
                  type="text"
                  value={formData.cardBankName}
                  onChange={(e) =>
                    setFormData({ ...formData, cardBankName: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Holder Name
                </label>
                <input
                  type="text"
                  value={formData.cardHolderName}
                  onChange={(e) =>
                    setFormData({ ...formData, cardHolderName: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {formData.paymentMethod === "bank_transfer" && (
          <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-md font-semibold mb-3">
              Bank Transfer Details
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Bank Details
                </label>
                <input
                  type="text"
                  value={formData.fromBankDetails}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fromBankDetails: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Source bank details"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Bank Details
                </label>
                <input
                  type="text"
                  value={formData.toBankDetails}
                  onChange={(e) =>
                    setFormData({ ...formData, toBankDetails: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Destination bank details"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction Reference
                </label>
                <input
                  type="text"
                  value={formData.transactionReference}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transactionReference: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bank reference number"
                />
              </div>
            </div>
          </div>
        )}
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Reason</label>
          <textarea
            value={formData.reason}
            onChange={(e) =>
              setFormData({ ...formData, reason: e.target.value })
            }
            className="w-full p-2 border rounded"
            rows="3"
          />
        </div>
      </div>
      <div className="flex space-x-2 mt-6">
        <button
          type="submit"
          disabled={
            loading ||
            (getValidationMessage() && getValidationMessage().type === "error")
          }
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? "Saving..."
            : editingTransfer
            ? "Update"
            : "Create Transfer"}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowForm(false);
            setEditingTransfer(null);
            resetForm();
          }}
          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  const renderTransfersList = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Internal Transfers</h3>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <FaExchangeAlt className="w-4 h-4" />
            <span>Add Transfer</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                From Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                To Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Payment Method
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transfers.map((transfer) => (
              <tr key={transfer.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(transfer.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    {transfer.from_asset_type === "cash" ? (
                      <FaMoneyBillWave className="w-4 h-4 mr-2 text-green-600" />
                    ) : (
                      <FaUniversity className="w-4 h-4 mr-2 text-blue-600" />
                    )}
                    {transfer.from_account_name ||
                      `${transfer.from_asset_type} - ${transfer.from_asset_id}`}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    {transfer.to_asset_type === "cash" ? (
                      <FaMoneyBillWave className="w-4 h-4 mr-2 text-green-600" />
                    ) : (
                      <FaUniversity className="w-4 h-4 mr-2 text-blue-600" />
                    )}
                    {transfer.to_account_name ||
                      `${transfer.to_asset_type} - ${transfer.to_asset_id}`}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  ${parseFloat(transfer.amount).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex flex-col">
                    <span>{transfer.payment_method}</span>
                    {transfer.payment_method === "cheque" && (
                      <div className="flex items-center space-x-1 mt-1">
                        {transfer.cheque_status && (
                          <span
                            className={`text-xs ${
                              transfer.cheque_status === "cleared"
                                ? "text-green-600"
                                : transfer.cheque_status === "bounced"
                                ? "text-red-600"
                                : transfer.cheque_status === "expired"
                                ? "text-gray-600"
                                : transfer.cheque_status === "rescheduled"
                                ? "text-yellow-600"
                                : "text-blue-600"
                            }`}
                          >
                            ({transfer.cheque_status})
                          </span>
                        )}
                        {!transfer.cheque_status && (
                          <span className="text-xs text-blue-600">
                            (pending)
                          </span>
                        )}
                        {transfer.cheque_reschedule_indicator === "rescheduled" && (
                          <span className="text-xs text-orange-600 font-medium">
                            ↻ rescheduled
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setEditingTransfer(transfer);
                      setFormData({
                        fromAssetType: transfer.from_asset_type,
                        fromAssetId: transfer.from_asset_id.toString(),
                        toAssetType: transfer.to_asset_type,
                        toAssetId: transfer.to_asset_id.toString(),
                        amount: transfer.amount,
                        reason: transfer.reason || "",
                        paymentMethod: transfer.payment_method,
                        chequeNumber: transfer.cheque_number || "",
                        chequeDate: transfer.cheque_date || "",
                        bankName: transfer.bank_name || "",
                        bankBranch: transfer.bank_branch || "",
                        cardTransactionId: transfer.card_transaction_id || "",
                        cardBankName: transfer.card_bank_name || "",
                        cardHolderName: transfer.card_holder_name || "",
                        fromBankDetails: transfer.from_bank_details || "",
                        toBankDetails: transfer.to_bank_details || "",
                        transactionReference:
                          transfer.transaction_reference || "",
                      });
                      setShowForm(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderChequesList = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold">
          Internal Transfer Cheque Management
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Cheque Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Bank
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                From → To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Reschedule Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Notes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cheques.map((cheque) => (
              <tr key={cheque.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {cheque.cheque_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(cheque.cheque_date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {cheque.bank_name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="flex flex-col">
                    <div className="font-medium">
                      {cheque.from_account_name}
                    </div>
                    <div className="text-gray-500 text-xs">↓</div>
                    <div className="font-medium">{cheque.to_account_name}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  ${parseFloat(cheque.amount_paid).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {cheque.previous_cheque_number && (
                    <div className="text-yellow-600">
                      <span className="text-xs">Rescheduled from:</span>
                      <br />
                      <span className="font-medium">
                        {cheque.previous_cheque_number}
                      </span>
                    </div>
                  )}
                  {cheque.next_cheque_number && (
                    <div className="text-blue-600">
                      <span className="text-xs">Rescheduled to:</span>
                      <br />
                      <span className="font-medium">
                        {cheque.next_cheque_number}
                      </span>
                    </div>
                  )}
                  {!cheque.previous_cheque_number &&
                    !cheque.next_cheque_number && (
                      <span className="text-gray-400">-</span>
                    )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      cheque.status
                    )}`}
                  >
                    {cheque.status || "pending"}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  {cheque.notes ? (
                    <div className="truncate" title={cheque.notes}>
                      {cheque.notes}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {!cheque.status &&
                    canUpdateChequeStatus(cheque.cheque_date) && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() =>
                            openChequeStatusModal(cheque.id, "cleared")
                          }
                          className="text-green-600 hover:text-green-900"
                          title="Mark as Cleared"
                        >
                          <FaCheck className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            openChequeStatusModal(cheque.id, "bounced")
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Mark as Bounced"
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            openChequeStatusModal(cheque.id, "expired")
                          }
                          className="text-gray-600 hover:text-gray-900"
                          title="Mark as Expired"
                        >
                          <FaClock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openRescheduleModal(cheque)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Reschedule"
                          disabled={!canRescheduleCheque(cheque)}
                        >
                          <FaExchangeAlt className="w-4 h-4" />
                        </button>
                        {(cheque.is_rescheduled ||
                          cheque.previous_cheque_number) && (
                          <button
                            onClick={() =>
                              fetchChequeHistory(cheque.payment_id)
                            }
                            className="text-blue-600 hover:text-blue-900"
                            title="View Cheque History"
                          >
                            <FaHistory className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  {!canUpdateChequeStatus(cheque.cheque_date) && (
                    <span className="text-gray-400 text-xs">Future date</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderChequeStatusModal = () => {
    if (!chequeStatusModal.show) return null;

    const getStatusLabel = (status) => {
      switch (status) {
        case "cleared":
          return "Clear";
        case "bounced":
          return "Mark as Bounced";
        case "expired":
          return "Mark as Expired";
        case "rescheduled":
          return "Reschedule";
        default:
          return "Update Status";
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case "cleared":
          return "bg-green-600 hover:bg-green-700";
        case "bounced":
          return "bg-red-600 hover:bg-red-700";
        case "expired":
          return "bg-gray-600 hover:bg-gray-700";
        case "rescheduled":
          return "bg-yellow-600 hover:bg-yellow-700";
        default:
          return "bg-blue-600 hover:bg-blue-700";
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-semibold mb-4">
            {getStatusLabel(chequeStatusModal.status)} Cheque
          </h3>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={chequeStatusModal.notes}
              onChange={(e) =>
                setChequeStatusModal({
                  ...chequeStatusModal,
                  notes: e.target.value,
                })
              }
              placeholder="Add any notes about this status update..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows="3"
              maxLength="500"
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {chequeStatusModal.notes.length}/500 characters
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleChequeStatusConfirm}
              disabled={loading}
              className={`flex-1 text-white px-4 py-2 rounded-lg font-medium transition-colors ${getStatusColor(
                chequeStatusModal.status
              )} disabled:opacity-50`}
            >
              {loading ? "Updating..." : "Confirm"}
            </button>
            <button
              onClick={() =>
                setChequeStatusModal({
                  show: false,
                  chequeId: null,
                  status: null,
                  notes: "",
                })
              }
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRescheduleModal = () => {
    if (!rescheduleModal.show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
          <h3 className="text-lg font-semibold mb-4">Reschedule Cheque</h3>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700">
              Current Cheque
            </h4>
            <p className="text-sm text-gray-600">
              Cheque Number: {rescheduleModal.oldChequeNumber}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Cheque Number *
              </label>
              <input
                type="text"
                value={rescheduleModal.newChequeNumber}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newChequeNumber: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new cheque number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Cheque Date *
              </label>
              <input
                type="date"
                value={rescheduleModal.newChequeDate}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newChequeDate: e.target.value,
                  })
                }
                min={new Date().toISOString().split("T")[0]}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                value={rescheduleModal.newBankName}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newBankName: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bank name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Branch
              </label>
              <input
                type="text"
                value={rescheduleModal.newBankBranch}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newBankBranch: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bank branch"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Rescheduling
              </label>
              <textarea
                value={rescheduleModal.notes}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    notes: e.target.value,
                  })
                }
                placeholder="Explain why the cheque is being rescheduled..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="3"
                maxLength="500"
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {rescheduleModal.notes.length}/500 characters
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleRescheduleSubmit}
              disabled={
                loading ||
                !rescheduleModal.newChequeNumber ||
                !rescheduleModal.newChequeDate
              }
              className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Rescheduling..." : "Reschedule Cheque"}
            </button>
            <button
              onClick={() =>
                setRescheduleModal({
                  show: false,
                  chequeId: null,
                  oldChequeNumber: "",
                  newChequeNumber: "",
                  newChequeDate: "",
                  newBankName: "",
                  newBankBranch: "",
                  notes: "",
                })
              }
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200/60">
        <div className="p-6 border-b border-slate-200/60">
          <h2 className="text-2xl font-bold text-slate-800">
            Internal Transfers
          </h2>
          <p className="text-slate-600 mt-1">
            Transfer money between your cash and bank accounts
          </p>
        </div>

        <div className="p-6">
          <div className="flex space-x-4 mb-6">
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === "transfers"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={() => setActiveTab("transfers")}
            >
              <FaList className="w-4 h-4 inline mr-2" />
              Transfers
            </button>
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === "cheques"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={() => setActiveTab("cheques")}
            >
              <FaCalendar className="w-4 h-4 inline mr-2" />
              Cheques
            </button>
          </div>

          {showForm && renderTransferForm()}
          {!showForm && activeTab === "transfers" && renderTransfersList()}
          {!showForm && activeTab === "cheques" && renderChequesList()}
        </div>
      </div>

      {/* Cheque Status Update Modal */}
      {renderChequeStatusModal()}

      {/* Cheque Reschedule Modal */}
      {renderRescheduleModal()}
    </div>
  );
};

export default InternalTransfers;
